/**
 * DesktopPresenter — Desktop UI strategy for e-commerce.
 *
 * Renders products via ProductPicker inside the shadow-root #ttpEcommerceArea,
 * manages panel expansion/shrinking, compact voice mode switching, and CartToast.
 *
 * Implements the EcommercePresenter interface.
 */
import { EcommercePresenter } from './EcommercePresenter.js';
import { ProductPicker } from './ProductPicker.js';
import { CartToast } from './CartToast.js';
import { FlyToCart } from './FlyToCart.js';
import { generateEcommerceCSS } from './ecommerceStyles.js';

export class DesktopPresenter extends EcommercePresenter {

  /**
   * @param {Object} config
   * @param {ShadowRoot} config.shadowRoot
   * @param {Function}   config.onProductAdd  - called when user clicks Add on a product
   * @param {Function}   config.onProductUndo - called when user clicks Undo on a toast
   */
  constructor(config = {}) {
    super();
    this.shadowRoot = config.shadowRoot;
    this.onProductAdd = config.onProductAdd || (() => {});
    this.onProductUndo = config.onProductUndo || (() => {});

    this.flyToCart = new FlyToCart({
      getCartTarget: () => this._getCartTarget(),
      onCartBump: () => this._triggerCartBump(),
      onRipple: () => this._triggerRipple(),
    });

    this.productPicker = new ProductPicker({
      primaryColor: config.primaryColor || '#7c3aed',
      onProductAdd: (cardEl, product) => this._handleProductAddWithFly(cardEl, product),
      onAddDisabled: () => this.flyToCart.isAnimating,
      onClose: () => this._handlePickerClose(),
    });
    this.cartToast = new CartToast({
      onUndo: (product) => this.onProductUndo(product),
      onHide: () => this._restoreWaveform(),
    });

    this.container = document.createElement('div');
    this.container.id = 'ttpEcommerceArea';
    this.container.className = 'ttp-ecommerce-area';
    /* Cart is appended to voiceActiveState (below input) in _attachToDOM — similar to mobile */
    const flyTarget = document.createElement('div');
    flyTarget.id = 'ttpDesktopFlyTarget';
    flyTarget.className = 'ttp-desktop-fly-target';
    flyTarget.setAttribute('aria-hidden', 'true');
    this.container.appendChild(flyTarget);

    this.voiceActiveState = null;
    this.isAttached = false;
    this._cartBump = false;
    this._ripple = false;
    this._toastActive = false;
    this._activatedByPresenter = false;
  }

  /**
   * Shadow root, or #ttp-widget-container when useShadowDOM is false (shadowRoot === document).
   * Prevents document-wide getElementById from hitting theme duplicates on Shopify.
   */
  _widgetDomRoot() {
    if (this.shadowRoot && this.shadowRoot !== document) {
      return this.shadowRoot;
    }
    return document.getElementById('ttp-widget-container');
  }

  _queryId(id) {
    const host = this._widgetDomRoot();
    if (!host) return null;
    return host.querySelector(`#${CSS.escape(id)}`);
  }

  _querySelector(sel) {
    const host = this._widgetDomRoot();
    if (!host) return null;
    return host.querySelector(sel);
  }

  _getCartTarget() {
    // Cart lives in voiceActiveState (below input); fallback to fly target in ecommerce area
    const voiceState = this.voiceActiveState || this._queryId('voiceActiveState');
    if (voiceState) {
      const icon = voiceState.querySelector('.ttp-cart-icon');
      if (icon) {
        const r = icon.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) return icon;
      }
    }
    const root = document.getElementById('ttpEcommerceArea') || this.container;
    return root?.querySelector('#ttpDesktopFlyTarget') || null;
  }

  _handleProductAddWithFly(cardEl, product) {
    if (this.flyToCart.isAnimating) return;
    this.productPicker.element?.classList.add('ttp-products-animating');
    this.flyToCart.triggerFly(cardEl, product, () => {
      this.productPicker.element?.classList.remove('ttp-products-animating');
      this.onProductAdd(product);
    }, { preferClone: false });
  }

  _triggerCartBump() {}

  _triggerRipple() {}

  // ═══════════════════════════════════════════
  // EcommercePresenter interface
  // ═══════════════════════════════════════════

  showProducts(products, title, layout) {
    this._ensureReady();
    this._ensureActiveStateVisible();
    this._switchToCompactMode();
    this._setContainerMode('products');

    this.productPicker.hide();

    const effectiveLayout = layout || (products.length <= 4 ? 'cards' : 'list');
    const pickerEl = this.productPicker.show(products, title, effectiveLayout);

    this.container.insertBefore(pickerEl, this.container.firstChild);
    this._expandPanelToFit(products.length, effectiveLayout);
  }

  /**
   * @param {Object} [opts]
   * @param {boolean} [opts.shrinkToToastHeight] - When true, shrink only to toast-visible height instead of original. Use when add-to-cart toast will be shown; full shrink happens when toast is gone.
   */
  hideProducts(opts = {}) {
    this.productPicker.hide();
    if (opts.shrinkToToastHeight || this._toastActive) {
      this._setPanelHeightForToast();
    } else {
      this._shrinkPanel();
    }
    this._restoreFullVoiceMode();
    this._setContainerMode('summary');
    if (!opts.shrinkToToastHeight && !this._toastActive) {
      this._restoreIdleStateIfNeeded();
    }
  }

  /** Height (px) for panel when toast is visible — minimal extra for toast, avoid large gap to conversation */
  static TOAST_HEIGHT_PX = 510;

  onProductAdded(product, cartInfo) {
    this._ensureReady();
    this._toastActive = true;
    /* Hide products and shrink to toast height (not original) — full shrink happens when toast is gone */
    this.hideProducts({ shrinkToToastHeight: true });

    const toastEl = this.cartToast.show(product, 'added');
    const controls = this._querySelector('.ttp-controls');
    if (controls) {
      controls.parentNode.insertBefore(toastEl, controls);
    } else if (this.voiceActiveState) {
      this.voiceActiveState.appendChild(toastEl);
    }
  }

  /** Set panel to toast-visible height (shrink from product list, but not to original) */
  _setPanelHeightForToast() {
    const apply = () => {
      const panel = this._queryId('text-chat-panel');
      if (!panel) return;
      const height = Math.min(DesktopPresenter.TOAST_HEIGHT_PX, Math.floor(window.innerHeight * 0.92));
      panel.classList.add('ttp-panel-expanded');
      panel.style.setProperty('height', `${height}px`, 'important');
    };
    apply();
    requestAnimationFrame(() => requestAnimationFrame(apply));
  }

  /** Shrink panel to original when toast is gone */
  _shrinkPanelForToast() {
    this._toastActive = false;
    const panel = this._queryId('text-chat-panel');
    if (panel) {
      panel.classList.remove('ttp-panel-expanded');
      panel.style.removeProperty('height');
    }
  }

  _restoreWaveform() {
    this._shrinkPanelForToast();
  }

  updateCart(_cartInfo) {}

  destroy() {
    if (this.flyToCart) this.flyToCart.destroy();
    this.productPicker.hide();
    this.cartToast.hide();
    this._shrinkPanel();
    this._restoreFullVoiceMode();
    if (this.voiceActiveState) {
      this.voiceActiveState.classList.remove('ttp-products-visible');
    }
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this.isAttached = false;
  }

  // ═══════════════════════════════════════════
  // DOM setup
  // ═══════════════════════════════════════════

  _ensureReady() {
    if (!this.isAttached) {
      this._injectStyles();
      this._attachToDOM();
    }
  }

  _injectStyles() {
    const css = generateEcommerceCSS();
    const root = this.shadowRoot;
    if (!root) return;

    /* useShadowDOM: false → shadowRoot is document; never use querySelector('style') (hits theme first). */
    if (root === document) {
      if (document.getElementById('ttp-ecommerce-presenter-styles')) return;
      const style = document.createElement('style');
      style.id = 'ttp-ecommerce-presenter-styles';
      /* Extra specificity + min heights for Shopify themes that collapse flex or steal #id matches */
      const scopedShopify = `
#ttp-widget-container #voiceActiveState.ttp-products-visible .picker-relative,
#ttp-widget-container #voiceActiveState.ttp-products-visible .ttp-ecommerce-area {
  min-height: 160px !important;
}
#ttp-widget-container #voiceActiveState.ttp-products-visible .ttp-product-picker {
  min-height: 140px !important;
}
`;
      style.textContent = css + scopedShopify;
      document.head.appendChild(style);
      return;
    }

    const existingStyle = root.querySelector('style');
    if (existingStyle) {
      existingStyle.textContent += '\n' + css;
    } else {
      const style = document.createElement('style');
      style.textContent = css;
      root.appendChild(style);
    }
  }

  _attachToDOM() {
    if (this.isAttached || !this.shadowRoot) return;

    this.voiceActiveState = this._queryId('voiceActiveState');
    if (!this.voiceActiveState) {
      console.warn('[DesktopPresenter] #voiceActiveState not found — will retry on next message');
      return;
    }

    if (this.voiceActiveState.contains(this.container)) {
      this.isAttached = true;
      return;
    }

    const desktopSection = this.voiceActiveState.querySelector('.desktop-voice-section');
    const compactSection = this.voiceActiveState.querySelector('.compact-voice-section');
    const insertAfter = compactSection || desktopSection;

    if (insertAfter && insertAfter.nextSibling) {
      this.voiceActiveState.insertBefore(this.container, insertAfter.nextSibling);
    } else {
      this.voiceActiveState.appendChild(this.container);
    }

    this.isAttached = true;
  }

  // ═══════════════════════════════════════════
  // Panel / voice mode helpers
  // ═══════════════════════════════════════════

  _setContainerMode(mode) {
    if (mode === 'products') {
      this.container.style.flex = '1 1 0';
      this.container.style.minHeight = '0';
      this.container.style.overflowY = 'auto';
      if (this.voiceActiveState) this.voiceActiveState.classList.add('ttp-products-visible');
    } else {
      /* Summary mode: ecommerce area grows to fill, pushing conversation+input to bottom */
      this.container.style.flex = '1 1 0';
      this.container.style.minHeight = '0';
      this.container.style.overflowY = '';
      if (this.voiceActiveState) this.voiceActiveState.classList.remove('ttp-products-visible');
    }

  }

  static LIST_ITEM_HEIGHT = 90;
  static LIST_GAP = 6;
  static PICKER_HEADER_HEIGHT = 44;
  static PICKER_PADDING = 20;
  static MIN_VISIBLE_LIST_ITEMS = 4;

  _expandPanelToFit(productCount, layout) {
    if (!this._widgetDomRoot()) return;
    const panel = this._queryId('text-chat-panel');
    if (!panel) return;

    panel.classList.add('ttp-panel-expanded');

    const activeState = this._queryId('voiceActiveState');
    requestAnimationFrame(() => {
      const compact = activeState?.querySelector('.compact-voice-section');
      const picker = this.container?.querySelector('.ttp-product-picker');
      const transcript = activeState?.querySelector('.desktop-conversation-panel');
      const inputRow = activeState?.querySelector('.voice-text-input-area');

      let needed = 0;
      if (compact) needed += compact.offsetHeight;

      const measuredPicker = picker ? picker.scrollHeight + 18 : 0;

      if (layout === 'list') {
        const visible = Math.min(productCount, DesktopPresenter.MIN_VISIBLE_LIST_ITEMS);
        const minPicker = DesktopPresenter.PICKER_HEADER_HEIGHT
          + DesktopPresenter.PICKER_PADDING
          + (visible * DesktopPresenter.LIST_ITEM_HEIGHT)
          + ((visible - 1) * DesktopPresenter.LIST_GAP);
        needed += Math.max(measuredPicker, minPicker);
      } else {
        needed += measuredPicker;
      }

      if (transcript) needed += Math.min(transcript.offsetHeight, 100);
      if (inputRow) needed += inputRow.offsetHeight;
      needed += 60;

      const maxHeight = Math.min(800, Math.floor(window.innerHeight * 0.9));
      const height = Math.min(Math.max(needed, 380), maxHeight);
      panel.style.setProperty('height', `${height}px`, 'important');
    });
  }

  _shrinkPanel() {
    if (!this._widgetDomRoot()) return;
    const panel = this._queryId('text-chat-panel');
    if (panel) {
      panel.classList.remove('ttp-panel-expanded');
      panel.style.removeProperty('height');
    }
  }

  _switchToCompactMode() {
    if (!this._widgetDomRoot()) return;
    const originalSection = this._queryId('desktopVoiceSection');
    const compactSection = this._queryId('compactVoiceSection');
    if (originalSection) originalSection.style.display = 'none';
    if (compactSection) compactSection.style.display = 'flex';
  }

  _restoreFullVoiceMode() {
    if (!this._widgetDomRoot()) return;
    const originalSection = this._queryId('desktopVoiceSection');
    const compactSection = this._queryId('compactVoiceSection');
    if (originalSection && compactSection) {
      originalSection.style.display = '';
      compactSection.style.display = 'none';
    }
  }

  _ensureActiveStateVisible() {
    if (!this._widgetDomRoot()) return;

    const panel = this._queryId('text-chat-panel');
    if (panel && !panel.classList.contains('open')) {
      panel.classList.add('open');
    }

    const landing = this._queryId('landingScreen');
    if (landing) landing.classList.remove('active');

    const textIf = this._queryId('textInterface');
    if (textIf) textIf.classList.remove('active');

    const vi = this._queryId('voiceInterface');
    if (vi && !vi.classList.contains('active')) {
      vi.classList.add('active');
    }

    const activeState = this._queryId('voiceActiveState');
    if (!activeState) return;

    const isHidden = activeState.style.display === 'none' ||
      getComputedStyle(activeState).display === 'none';
    if (isHidden) {
      const idleState = this._queryId('voiceIdleState');
      if (idleState) idleState.style.display = 'none';
      activeState.style.display = 'flex';
      this._activatedByPresenter = true;
    }
  }

  _restoreIdleStateIfNeeded() {
    if (!this._activatedByPresenter || !this._widgetDomRoot()) return;
    const activeState = this._queryId('voiceActiveState');
    const idleState = this._queryId('voiceIdleState');
    if (activeState) activeState.style.display = 'none';
    if (idleState) idleState.style.display = 'flex';
    this._activatedByPresenter = false;
  }

  _handlePickerClose() {
    this.hideProducts();
  }
}
